import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { ArrowUpRight, ArrowLeft, RefreshCw, ShieldCheck } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const BridgeMark = ({ className }) => (
  <svg viewBox="0 0 36 28" fill="none" className={className} aria-hidden="true">
    <rect x="1.5" y="13" width="3" height="14" rx="1.5" fill="currentColor" />
    <rect x="31.5" y="13" width="3" height="14" rx="1.5" fill="currentColor" />
    <path d="M3 13 Q18 1 33 13" stroke="currentColor" strokeWidth="2.5" fill="none" strokeLinecap="round" />
    <line x1="0" y1="21" x2="36" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <line x1="10.5" y1="7" x2="10.5" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="18" y1="3" x2="18" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    <line x1="25.5" y1="7" x2="25.5" y2="21" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

// ── Teacher OTP flow ──────────────────────────────────────────────────────────

function TeacherLogin({ onSuccess }) {
  const [step, setStep] = useState(1); // 1=details, 2=verify
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [otpCode, setOtpCode] = useState(""); // the generated OTP to display
  const [entered, setEntered] = useState(""); // what the teacher types
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const cooldownRef = useRef(null);

  useEffect(() => () => clearInterval(cooldownRef.current), []);

  const startCooldown = () => {
    setResendCooldown(30);
    cooldownRef.current = setInterval(() => {
      setResendCooldown((c) => {
        if (c <= 1) { clearInterval(cooldownRef.current); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const requestOtp = async (e) => {
    e?.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/request-otp", { name, mobile });
      setOtpCode(data.otp);
      setEntered("");
      setStep(2);
      startCooldown();
      toast.success("OTP generated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const verify = async (e) => {
    e?.preventDefault();
    if (entered.length !== 6) { toast.error("Enter the 6-digit OTP"); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/auth/verify-otp", { mobile, otp: entered });
      toast.success(`Welcome, ${data.name}`);
      onSuccess(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence mode="wait">
      {step === 1 ? (
        <motion.form
          key="step1"
          initial={{ opacity: 0, x: -16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: 16 }}
          transition={{ duration: 0.25 }}
          onSubmit={requestOtp}
          className="space-y-4"
        >
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">Your name</Label>
            <Input
              data-testid="teacher-name-input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Sunita Patil"
              required
              className="mt-1.5 rounded-xl border-[#E8D5C8] bg-white py-5 focus:border-[#E8542A]"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">Mobile number</Label>
            <Input
              data-testid="teacher-mobile-input"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="10-digit number"
              required
              className="mt-1.5 rounded-xl border-[#E8D5C8] bg-white py-5 focus:border-[#E8542A]"
            />
          </div>
          <Button
            type="submit"
            data-testid="get-otp-btn"
            disabled={loading}
            className="group flex w-full items-center justify-center gap-2 rounded-2xl bg-[#E8542A] py-5 text-sm font-bold text-white hover:bg-[#C94520]"
          >
            {loading ? "Generating OTP…" : "Get OTP"}
            {!loading && <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
          </Button>
          <p data-testid="teacher-login-note" className="text-center text-xs text-[#8A6D5C]">
            New here? Your account is created on first sign in.
          </p>
        </motion.form>
      ) : (
        <motion.form
          key="step2"
          initial={{ opacity: 0, x: 16 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -16 }}
          transition={{ duration: 0.25 }}
          onSubmit={verify}
          className="space-y-4"
        >
          {/* OTP display box */}
          <motion.div
            initial={{ scale: 0.92, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 280, damping: 22 }}
            data-testid="otp-display-box"
            className="flex flex-col items-center gap-2 rounded-2xl border-2 border-[#E8542A]/30 bg-[#E8542A]/5 px-6 py-5"
          >
            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#E8542A]">
              <ShieldCheck className="h-3.5 w-3.5" />
              Your OTP
            </div>
            <p
              data-testid="otp-code"
              className="font-mono text-5xl font-bold tracking-[0.25em] text-[#1A1918]"
            >
              {otpCode}
            </p>
            <p className="text-xs text-[#8A6D5C]">Valid for 5 minutes</p>
          </motion.div>

          <div>
            <Label className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">Enter the OTP above</Label>
            <Input
              data-testid="otp-input"
              value={entered}
              onChange={(e) => setEntered(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="6-digit code"
              inputMode="numeric"
              maxLength={6}
              required
              autoFocus
              className="mt-1.5 rounded-xl border-[#E8D5C8] bg-white py-5 text-center font-mono text-xl tracking-widest focus:border-[#E8542A]"
            />
          </div>

          <Button
            type="submit"
            data-testid="verify-otp-btn"
            disabled={loading || entered.length !== 6}
            className="group flex w-full items-center justify-center gap-2 rounded-2xl bg-[#E8542A] py-5 text-sm font-bold text-white hover:bg-[#C94520] disabled:opacity-50"
          >
            {loading ? "Verifying…" : "Verify & Sign In"}
            {!loading && <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />}
          </Button>

          <div className="flex items-center justify-between">
            <button
              type="button"
              data-testid="back-to-step1"
              onClick={() => setStep(1)}
              className="flex items-center gap-1 text-xs text-[#8A6D5C] transition-colors hover:text-[#E8542A]"
            >
              <ArrowLeft className="h-3 w-3" /> Change number
            </button>
            <button
              type="button"
              data-testid="resend-otp-btn"
              onClick={requestOtp}
              disabled={resendCooldown > 0 || loading}
              className="flex items-center gap-1 text-xs text-[#8A6D5C] transition-colors hover:text-[#E8542A] disabled:opacity-40"
            >
              <RefreshCw className="h-3 w-3" />
              {resendCooldown > 0 ? `Resend in ${resendCooldown}s` : "Resend OTP"}
            </button>
          </div>
        </motion.form>
      )}
    </AnimatePresence>
  );
}

// ── Admin login ───────────────────────────────────────────────────────────────

function AdminLogin({ onSuccess }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin-login", { email, password });
      toast.success(`Welcome, ${data.name || "admin"}`);
      onSuccess(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form
      initial={{ opacity: 0, x: 12 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -12 }}
      transition={{ duration: 0.2 }}
      onSubmit={submit}
      className="space-y-4"
    >
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">Admin email</Label>
        <Input
          type="email"
          data-testid="admin-email-input"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          className="mt-1.5 rounded-xl border-[#E8D5C8] bg-white py-5 focus:border-[#E8542A]"
        />
      </div>
      <div>
        <Label className="text-xs font-bold uppercase tracking-wider text-[#8A6D5C]">Password</Label>
        <Input
          type="password"
          data-testid="admin-password-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className="mt-1.5 rounded-xl border-[#E8D5C8] bg-white py-5 focus:border-[#E8542A]"
        />
      </div>
      <Button
        type="submit"
        data-testid="login-submit-btn"
        disabled={loading}
        className="group flex w-full items-center justify-center gap-2 rounded-2xl bg-[#E8542A] py-5 text-sm font-bold text-white hover:bg-[#C94520]"
      >
        {loading ? "Signing in…" : "Sign in as admin"}
        {!loading && <ArrowUpRight className="h-4 w-4" />}
      </Button>
    </motion.form>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function Login() {
  const navigate = useNavigate();
  const [mode, setMode] = useState("teacher");

  const onSuccess = (user) => {
    navigate(user.role === "admin" ? "/admin" : "/dashboard");
  };

  return (
    <main data-testid="login-page" className="flex min-h-screen bg-[#0D0C0B]">
      {/* Left branding panel */}
      <div className="relative hidden flex-col justify-between overflow-hidden bg-[#0D0C0B] p-12 lg:flex lg:w-1/2">
        <div
          className="pointer-events-none absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
            backgroundRepeat: "repeat",
            backgroundSize: "128px",
          }}
        />
        <div className="pointer-events-none absolute bottom-0 left-0 h-96 w-96 -translate-x-1/3 rounded-full bg-[#E8542A] opacity-10 blur-[100px]" />
        <Link to="/" className="flex items-center gap-2.5 text-[#E8542A]">
          <BridgeMark className="h-7 w-9" />
          <span className="font-display text-2xl font-bold">ClassBridge</span>
        </Link>
        <div className="z-10">
          <h2 className="font-display text-5xl font-bold leading-[1.1] text-[#F5F0EB]">
            Classroom to<br />
            <span className="text-[#E8542A]">parents,</span><br />
            every day.
          </h2>
          <p className="mt-6 text-base leading-relaxed text-[#5C4E46]">
            Record your class, get an AI transcript, and send parents a beautiful update on WhatsApp — in seconds.
          </p>
        </div>
        <div className="z-10 flex items-center gap-6 text-xs text-[#3A3028]">
          <span>English · हिंदी · मराठी</span>
          <span className="h-3 w-[1px] bg-white/10" />
          <span>Free to start</span>
        </div>
      </div>

      {/* Right form panel */}
      <div className="flex flex-1 flex-col items-center justify-center bg-[#FDF3EC] px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-sm"
        >
          <Link to="/" data-testid="login-brand" className="mb-8 flex items-center gap-2 text-[#E8542A] lg:hidden">
            <BridgeMark className="h-6 w-8" />
            <span className="font-display text-xl font-bold">ClassBridge</span>
          </Link>

          <h1 className="font-display text-3xl font-bold text-[#1A1918]">Welcome back</h1>
          <p className="mt-1 text-sm text-[#8A6D5C]">Sign in to your ClassBridge account</p>

          {/* Tab switcher */}
          <div className="mt-8 flex rounded-2xl bg-[#F3EFEA] p-1">
            {["teacher", "admin"].map((m) => (
              <button
                key={m}
                type="button"
                data-testid={`login-mode-${m}`}
                onClick={() => setMode(m)}
                className={`flex-1 rounded-xl py-2.5 text-sm font-bold capitalize transition-all duration-200 ${
                  mode === m ? "bg-[#E8542A] text-white shadow-sm" : "text-[#8A6D5C] hover:text-[#E8542A]"
                }`}
              >
                {m}
              </button>
            ))}
          </div>

          <div className="mt-6">
            <AnimatePresence mode="wait">
              {mode === "teacher" ? (
                <motion.div key="teacher" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <TeacherLogin onSuccess={onSuccess} />
                </motion.div>
              ) : (
                <motion.div key="admin" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                  <AdminLogin onSuccess={onSuccess} />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </main>
  );
}
